import { I18n } from "@assets/strings";
import React from "react";
import styled, { css } from "styled-components";
import { Environment } from "@core/config/environment";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useIsPerson } from "@domain/currentProfile/hooks";
import { Post } from "@domain/post/post";
import { useAsync, useIsMobile } from "@utils/hooks";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, gap } from "@ui/styles/utils";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { CommentIcon, LikeIcon, RepostIcon, ShareIcon } from "@ui/components/icons";
import { EditReferencePostModal } from "@ui/components/post/reference/editReferencePostModal";
import { SharePostModal } from "./sharePostModal";

interface PostActionsProps extends React.HTMLAttributes<HTMLDivElement> {
	post: Post;
	onCommentAction?: () => void;
}

export const PostActions: React.FC<PostActionsProps> = ({ post, onCommentAction, ...htmlProps }) => {
	const { postService, trackingService } = useServices();
	const currentProfile = useCurrentProfile();
	const { createModal } = useModalTools();
	const isMobile = useIsMobile();
	const isAuthor = useIsPerson(post.author);

	const { run: like, loading: liking } = useAsync(async () => {
		await postService.togglePostLike(post);
		return trackingService.track("Feed - post liked", {
			user: currentProfile?.primaryName,
			contentType: post.type,
		});
	}, [post]);

	const blockGuest = useAuthModalBlocker();

	const openSocialSharingMobile = async (): Promise<void> => {
		try {
			await navigator.share({
				text: I18n.t("social_sharing.message"),
				url: `${Environment.APP_BASE_URL}${Path.Post(post.id)}`,
			});
		} catch (error) {
			console.log("error", error);
		}
	};

	// const {createModal}=useModalTools()

	return (
		<Container {...htmlProps} isMobile={isMobile}>
			{isAuthor ? null : (
				<LikeAction liked={post.likes.byMe} onClick={() => blockGuest(() => !liking && like())}>
					<LikeIcon /> {!isMobile && I18n.t("post.action.like")}
				</LikeAction>
			)}
			<Action onClick={() => blockGuest(() => onCommentAction?.())}>
				<CommentIcon />
				{!isMobile && I18n.t("post.action.comment")}
			</Action>
			{isAuthor ? null : (
				<Action
					onClick={() =>
						blockGuest(() =>
							createModal((close) => (
								<EditReferencePostModal close={close} shared={{ type: "SHARE_POST", reference: post }} />
							))
						)
					}
				>
					<RepostIcon /> {!isMobile && I18n.t("post.action.share")}
				</Action>
			)}
			<Action
				onClick={() =>
					blockGuest(() =>
						isMobile ? openSocialSharingMobile() : createModal((close) => <SharePostModal post={post} close={close} />)
					)
				}
			>
				<ShareIcon /> {!isMobile && I18n.t("post.action.share_outside")}
			</Action>
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile: boolean }>`
	display: flex;
	align-items: center;
	${gap(16, "row")}
	${({ isMobile }) => isMobile && `justify-content:start`}
`;

const menuActionStyle = css`
	cursor: pointer;
	display: flex;
	align-items: center;
	${({ theme }) => theme.textStyles.label};
	svg {
		margin-right: 3px;
		width: 18px;
		height: 18px;
	}

	@media ${desktop} {
		&:hover {
			color: ${({ theme }) => theme.colors.gray};
			svg path {
				fill: ${({ theme }) => theme.colors.gray};
			}
		}
	}
`;

const Action = styled.div`
	${menuActionStyle}
`;

const LikeAction = styled(Action).withConfig({
	shouldForwardProp: (prop) => prop !== "liked",
})<{ liked?: boolean }>`
	${({ theme, liked }) =>
		liked &&
		css`
			color: ${theme.colors.primary};
			svg path {
				fill: ${theme.colors.primary};
			}
			&:hover {
				color: ${theme.colors.primary};
				svg path {
					fill: ${theme.colors.primary};
				}
			}
		`};
`;
